/**
 * Admin Symbol Service
 * Handles all symbol-related admin API calls
 */

import { getApi, postApi, patchApi } from '../adminApi';

/**
 * Get all symbols
 * @param {Object} params - Query parameters (type: 'forex' | 'crypto' | 'gold')
 * @returns {Promise}
 */
export const getAllSymbols = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const endpoint = queryParams ? `symbols/all?${queryParams}` : 'symbols/all';
  return getApi(endpoint);
};

/**
 * Get symbols by type
 * @param {string} type - Symbol type ('forex', 'crypto', 'gold')
 * @returns {Promise}
 */
export const getSymbolsByType = async (type) => {
  return getApi(`symbols/all?type=${type}`);
};

/**
 * Get symbol by ID
 * @param {string} symbolId - Symbol ID
 * @returns {Promise}
 */
export const getSymbolById = async (symbolId) => {
  return getApi(`symbols/${symbolId}`);
};

/**
 * Create a new symbol
 * @param {Object|FormData} formData - Symbol data (can be FormData for file uploads)
 * @returns {Promise}
 */
export const createSymbol = async (formData) => {
  if (formData instanceof FormData) {
    return postApi('symbols', formData, { useFormData: true });
  }
  return postApi('symbols', formData);
};

/**
 * Create crypto symbol
 * @param {Object} data - Crypto symbol data
 * @returns {Promise}
 */
export const createCryptoSymbol = async (data) => {
  return createSymbol(data);
};

/**
 * Create forex symbol
 * @param {Object} data - Forex symbol data
 * @returns {Promise}
 */
export const createForexSymbol = async (data) => {
  return createSymbol(data);
};

/**
 * Create gold symbol
 * @param {Object} data - Gold symbol data
 * @returns {Promise}
 */
export const createGoldSymbol = async (data) => {
  return createSymbol(data);
};

/**
 * Update symbol status
 * @param {string} symbolId - Symbol ID
 * @param {string} status - New status ('active' | 'inactive')
 * @returns {Promise}
 */
export const updateSymbolStatus = async (symbolId, status) => {
  return postApi(`symbols/${symbolId}/status`, { status });
};

/**
 * Import symbols from TradingView (Forex)
 * @returns {Promise}
 */
export const importForexSymbolsFromTradingView = async () => {
  return postApi('symbols/tradingview/forex/import');
};

/**
 * Import symbols from TradingView (Gold)
 * @returns {Promise}
 */
export const importGoldSymbolsFromTradingView = async () => {
  return postApi('symbols/tradingview/gold/import');
};

/**
 * Fetch symbols from Binance
 * @returns {Promise}
 */
export const fetchSymbolsFromBinance = async () => {
  return postApi('symbols/binance/fetch');
};

